"use client";

import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type DonateStep = "amount" | "donor";

const STEPS = [
  { id: "amount" as const, label: "Amount", number: 1 },
  { id: "donor" as const, label: "Your Details", number: 2 },
];

interface DonateStepperProps {
  step: DonateStep;
  className?: string;
  variant?: "light" | "dark";
}

export function DonateStepper({
  step,
  className,
  variant = "light",
}: DonateStepperProps) {
  const isDark = variant === "dark";

  return (
    <nav
      aria-label="Donation steps"
      className={cn("mx-auto w-full max-w-md", className)}
    >
      <ol className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const isActive = step === s.id;
          const isComplete = step === "donor" && s.id === "amount";

          return (
            <li key={s.id} className="flex flex-1 items-center gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors",
                    isComplete || isActive
                      ? isDark
                        ? "bg-white text-primary"
                        : "bg-[var(--charity-green-dark)] text-white"
                      : isDark
                        ? "border border-white/40 text-white/70"
                        : "border border-border text-muted"
                  )}
                  aria-current={isActive ? "step" : undefined}
                >
                  {isComplete ? <Check size={14} aria-hidden /> : s.number}
                </span>
                <span
                  className={cn(
                    "hidden text-sm font-medium sm:inline",
                    isActive || isComplete
                      ? isDark
                        ? "text-white"
                        : "text-foreground"
                      : isDark
                        ? "text-white/60"
                        : "text-muted"
                  )}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={cn(
                    "h-px flex-1",
                    isComplete
                      ? isDark
                        ? "bg-white/80"
                        : "bg-[var(--charity-green-dark)]/50"
                      : isDark
                        ? "bg-white/25"
                        : "bg-border"
                  )}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
